import { useContext, useState } from 'react';
import { AdminProjects } from '@platform/client';
import { AdminTableContext } from '../../AdminTable';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, Spinner } from '@platform/components';
import { FileUploadWithButton } from '../../components/FileUploadWithButton';
import { GreenCircleCheckmark } from '../../../svg/GreenCircleCheckmark';
import { HTTPValidationError } from '@platform/client/schemas';

const PROJECT_IMPORT_TEMPLATE =
  'https://movingworldspublic.blob.core.windows.net/public/project_import_template_v2.csv';

export const ImportProjectsModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File>();
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const batchImportProjectMutation = AdminProjects.useBatchImportProjects({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...AdminProjects.getGetProjectsQueryKey({ ...adminQueryOptionsV2 })],
        });
      },
    },
  });
  const handleClose = () => {
    setFile(undefined);
    batchImportProjectMutation.reset();
    onClose();
  };
  const isDone = batchImportProjectMutation.isError || batchImportProjectMutation.isSuccess;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      variant='dialog'
      headerDivision='divider'
      title='Import Projects'
      ignoreExtraPadding
      primaryCTA={{
        text: isDone ? 'Close' : 'Import Projects',
        callback:
          isDone ? handleClose : (
            () => file && batchImportProjectMutation.mutate({ data: { file } })
          ),
        isDisabled: () => !file || batchImportProjectMutation.isPending,
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: handleClose,
      }}
    >
      <div className='flex flex-col justify-center items-center gap-4'>
        {!batchImportProjectMutation.isPending &&
          !batchImportProjectMutation.isError &&
          !batchImportProjectMutation.isSuccess && (
            <>
              <p>
                Upload the completed project template below. You can download a copy{' '}
                <a className='blue-link' href={PROJECT_IMPORT_TEMPLATE} download>
                  here
                </a>
                .
              </p>
              <div className='max-w-48'>
                <FileUploadWithButton
                  onFileUpload={(file) => setFile(file)}
                  acceptedFileTypes={['.csv']}
                />
              </div>
              {file && <p className='text-xs text-gray-400'>File name: {file.name}</p>}
            </>
          )}
        {batchImportProjectMutation.isPending && (
          <>
            <Spinner />
            <p>Importing projects from {file?.name}. This may take a few moments.</p>
          </>
        )}
        {batchImportProjectMutation.isSuccess && (
          <>
            <GreenCircleCheckmark size={24} />
            <p>Projects submitted successfully</p>
          </>
        )}
        {batchImportProjectMutation.isError && (
          <>
            <p>There was an error importing the file:</p>
            {(batchImportProjectMutation.error as HTTPValidationError)?.detail?.map((err, i) => (
              <p key={i}>{err.msg}</p>
            ))}
            <p>
              Review the import file for missing values. If the issue continues contact tech team!
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};
